import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AllViewCountActionFetchData } from '../../../redux/actions/AllViewCountAction';
import { FaSearch, FaTimes, FaChevronDown } from 'react-icons/fa';
import './AssemblyEle.css';
import axios from 'axios';
import HttpService from '../../../utils/httpService';
import HomeTaboola from '../HomeTaboola';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactGA from "react-ga4";

const AssemblyEle = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();

    const [overallData, setOverallData] = useState([]);
    const [selectedState, setSelectedState] = useState(null);
    const [nestedTableData, setNestedTableData] = useState({});
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const locationDropdownRef = useRef(null);
    const resultsRef = useRef(null);
    const tabsScrollRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
    const imgBaseUrl = "https://epapercdn.sandesh.com/";
    const scrollToResults = () => {
        if (resultsRef.current) {
            resultsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const queryParams = new URLSearchParams(location.search);
    const urlStateName = queryParams.get('state');

    const slugify = (text) => {
        return (text || "").toString().toLowerCase().trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w-]+/g, '')
            .replace(/--+/g, '-');
    };

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        dispatch(AllViewCountActionFetchData(window.location.pathname));

        // Fetch Multiple States Summary
        axios.get(`${HttpService.seatTally}`).then((res) => {
            const responseData = res?.data;
            const fetchedData = responseData?.data || (Array.isArray(responseData) ? responseData : []);

            if (fetchedData && fetchedData.length > 0) {
                const customOrder = ["WESTBENGAL", "TAMILNADU", "ASSAM", "KERALA", "PUDUCHERRY"];
                const sortedData = [...fetchedData].sort((a, b) => {
                    const nameA = (a.state_name || "").replace(/\s/g, "").toUpperCase();
                    const nameB = (b.state_name || "").replace(/\s/g, "").toUpperCase();
                    const indexA = customOrder.indexOf(nameA);
                    const indexB = customOrder.indexOf(nameB);
                    if (indexA === -1 && indexB === -1) return 0;
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;
                    return indexA - indexB;
                });

                setOverallData(sortedData);

                // Select state from URL name or default to West Bengal
                let targetState = null;
                if (urlStateName) {
                    targetState = sortedData.find(s => slugify(s.state_name) === urlStateName);
                }

                // If no valid URL param, try to find West Bengal explicitly, otherwise take the first item
                if (!targetState) {
                    targetState = sortedData.find(s => slugify(s.state_name) === "west-bengal") || sortedData[0];
                }

                setSelectedState(targetState);
            } else {
                console.warn("API returned empty data.");
                setOverallData([]);
                setSelectedState(null);
            }
        }).catch((err) => {
            console.error("Seat Tally API Error:", err);
            setOverallData([]);
            setSelectedState(null);
        });
    }, [dispatch, urlStateName]);

    const fetchNestedData = useCallback((stateId) => {
        axios.get(`${HttpService.cityDataNested}?state_id=${stateId}&election_id=5`)
            .then((res) => {
                const data = res?.data?.data || {};
                setNestedTableData(data);
                const districts = Object.keys(data);
                if (districts.length > 0) {
                    setSelectedDistrict(districts[0]);
                }
            })
            .catch(() => {
                setNestedTableData({});
                setSelectedDistrict(null);
            });
    }, []);

    useEffect(() => {
        if (selectedState) {
            fetchNestedData(selectedState.state_id);
            // GA Tracking for state selection
            ReactGA.event({
                category: "Assembly Election",
                action: "State Selected",
                label: selectedState.state_name
            });
        }
    }, [selectedState, fetchNestedData]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                setIsLocationDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Sync horizontal scroll for district tabs
    useEffect(() => {
        if (selectedDistrict && tabsScrollRef.current) {
            const container = tabsScrollRef.current;
            const activeTab = container.querySelector('.dist-tab-pill.active');
            if (activeTab) {
                const scrollTarget = activeTab.offsetLeft - (container.offsetWidth / 2) + (activeTab.offsetWidth / 2);
                container.scrollTo({
                    left: scrollTarget,
                    behavior: 'smooth'
                });
            }
        }
    }, [selectedDistrict]);

    const getProgress = (votes) => {
        if (!votes || typeof votes !== 'string' || !votes.includes('/')) return 0;
        const [done, total] = votes.split('/');
        const pct = (parseInt(done) / parseInt(total)) * 100;
        return isNaN(pct) ? 0 : pct;
    };

    const hexToRGBA = (hex, alpha) => {
        if (!hex || !hex.startsWith('#')) return `rgba(100, 100, 100, ${alpha})`;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const displayData = selectedState || {};

    const sortedParties = displayData?.parties
        ? [...displayData.parties].sort((a, b) => {
            if (a.short_name === 'OTH') return 1;
            if (b.short_name === 'OTH') return -1;
            return 0;
        })
        : [];

    const activeDistricts = Object.keys(nestedTableData);


    const activeDistrictData = nestedTableData[selectedDistrict] || [];
    const filteredConsts = activeDistrictData.filter(c =>
        selectedDistrict?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <div className="assembly-ele-dashboard container">
                <header className="simple-header">
                    <div className="header-content">
                        <div className="title-group">
                            <span className="brand-badge">ELECTION 2026</span>
                            <h1 className="header-title">
                                <span className="guj-title">વિધાનસભા ચૂંટણી <span className="highlight-2027">લાઇવ પરિણામો</span></span>
                            </h1>
                        </div>
                        <div className="header-meta">
                            <div className="live-badge">
                                <span className="pulse-dot"></span>
                                LIVE RESULTS
                            </div>
                            <div className="time-stamp">Updated: Just Now</div>
                        </div>
                    </div>
                </header>


                <div className="ele-dashboard-main-vessel">
                    <div className="dashboard-grid">
                        <div className="tier-panel">
                            <h2 className="panel-label">Select State</h2>
                            <div className="tac-card-stack">
                                {overallData.map((state, index) => (
                                    <div
                                        className={`tac-tier-card cursor-pointer ${displayData?.state_id === state.state_id ? 'active' : ''}`}
                                        key={index}
                                        onClick={() => {
                                            setSelectedState(state);
                                            // Update URL with state name to preserve selection on reload
                                            navigate(`/assembly-election?state=${slugify(state.state_name)}`, { replace: true });
                                        }}
                                    >
                                        <div className="tac-name-stack">
                                            <h3 className="tac-guj">{state.sstate_name_guj || state.state_name_guj}</h3>
                                            <h3 className="tac-eng">{state.state_name}</h3>
                                        </div>
                                        {displayData?.state_id === state.state_id && (
                                            <div className="tac-active-glow" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="tactical-glass-vault animate-vault-reveal">
                            <div className="vault-flex-container">
                                <div className="vault-map-block cursor-pointer" onClick={scrollToResults}>
                                    <div className="stage-digital-grid" />
                                    <div className="map-scanning-laser" />
                                    <div className="map-hud-brackets" />

                                    {displayData?.state_image ? (
                                        displayData.state_image.endsWith('.mp4') ? (
                                            <video
                                                src={imgBaseUrl + displayData.state_image}
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className="hero-map-tactical animate-map-float"
                                                key={displayData.state_image}
                                            />
                                        ) : (
                                            <img
                                                src={imgBaseUrl + displayData.state_image}
                                                alt={displayData.state_name}
                                                className="hero-map-tactical animate-map-float"
                                                key={displayData.state_image}
                                            />
                                        )
                                    ) : (
                                        <div className="no-map-placeholder" />
                                    )}

                                    <div className="stage-hud-ring">
                                        <svg viewBox="0 0 100 100" className="hud-svg">
                                            <circle cx="50" cy="50" r="45" className="ring-base" />
                                            <circle
                                                cx="50" cy="50" r="45"
                                                className="ring-fill"
                                                style={{ strokeDashoffset: 283 - (283 * getProgress(displayData?.displayTally || '0/100')) / 100 }}
                                            />
                                        </svg>
                                        <div className="hud-content">
                                            <span className="hud-val">{getProgress(displayData?.displayTally || '0/0').toFixed(0)}%</span>
                                            <span className="hud-lbl">RESULTS</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="vault-stats-rail">
                                    <div className="rail-header">
                                        <div className="r-badge">
                                            <div className="b-dot" />
                                            LIVE TRANSMISSION
                                        </div>
                                        <h2 className="r-guj-name">
                                            {displayData?.state_name_guj || displayData?.state_name}
                                            <span className="r-eng-bracket">({displayData?.state_name})</span>
                                        </h2>
                                    </div>

                                    <div className="rail-summary-stats">
                                        <div className="r-sum-box cursor-pointer" onClick={scrollToResults}>
                                            <span className="r-s-val">{displayData?.displayTally || '0/0'}</span>
                                            <span className="r-s-lbl">DECLARED</span>
                                        </div>
                                        <div className="r-v-divider" />
                                        <div className="r-sum-box cursor-pointer" onClick={scrollToResults}>
                                            <span className="r-s-val">{displayData?.totalSeats || 0}</span>
                                            <span className="r-s-lbl">TOTAL SEATS</span>
                                        </div>
                                    </div>

                                    <div className="rail-party-standings">
                                        <div className="section-divider">LIVE STANDINGS</div>

                                        <div className="standings-row">
                                            {sortedParties?.filter(p => p.name && p.short_name).map((party, idx) => {
                                                const seats = party.seats_won || 0;
                                                const maxSeats = Math.max(...sortedParties.map(p => p.seats_won || 0));
                                                const isLeader = seats > 0 && seats === maxSeats;

                                                const partyColor = party.short_name === 'OTH' ? '#0ea5e9' : party.color_code;
                                                return (
                                                    <div
                                                        className={`p-mini-card cursor-pointer ${isLeader ? 'is-leader' : ''}`}
                                                        key={idx}
                                                        onClick={scrollToResults}
                                                        style={{
                                                            borderTopColor: partyColor,
                                                            color: partyColor
                                                        }}
                                                    >
                                                        <div className="p-m-header">
                                                            <div className="p-m-logo">
                                                                <img src={party.logo?.startsWith('http') ? party.logo : (imgBaseUrl + party.logo)} alt={party.short_name} />
                                                            </div>
                                                            <div className="p-m-info">
                                                                <span className="p-m-short">{party.short_name}</span>
                                                                <span className="p-m-full">{party.name}</span>
                                                            </div>
                                                        </div>
                                                        <div className="p-m-score" style={{ color: partyColor }}>{seats}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="table-header-row" ref={resultsRef}>
                        <h3 className="panel-label">
                            Constituency{selectedDistrict && ` — ${selectedDistrict}`}
                        </h3>
                        <div className="table-header-actions">
                            <div className="dropdown-group" ref={locationDropdownRef}>
                                <span className="dropdown-hint">SELECT DIST:</span>
                                <div className={`custom-glass-dropdown ${isLocationDropdownOpen ? 'open' : ''}`}>
                                    <div
                                        className="dropdown-trigger"
                                        onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                                    >
                                        <span className="trigger-text">{selectedDistrict || 'Select'}</span>
                                        <span className="trigger-icon"><FaChevronDown /></span>
                                    </div>

                                    {isLocationDropdownOpen && (
                                        <div className="dropdown-menu-vessel animate-dropdown">
                                            {activeDistricts.map((dist, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`dropdown-item-option ${selectedDistrict === dist ? 'active' : ''}`}
                                                    onClick={() => {
                                                        setSelectedDistrict(dist);
                                                        setIsLocationDropdownOpen(false);
                                                    }}
                                                >
                                                    <span className="item-dot" />
                                                    {dist}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="table-search-box">
                                <span className="search-icon"><FaSearch /></span>
                                <input
                                    type="text"
                                    placeholder="Search District or Constituency..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="table-search-input"
                                />
                                {searchTerm && (
                                    <button className="search-clear-btn" onClick={() => setSearchTerm('')}>
                                        <FaTimes />
                                    </button>
                                )}
                            </div>
                            <div className="table-count-badge">
                                Showing <strong>{filteredConsts.length}</strong> Locations
                            </div>
                        </div>
                    </div>

                    {/* District Tabs */}
                    {activeDistricts.length > 0 && (
                        <div className="district-tabs-container">
                            <div className="district-tabs-scroll" ref={tabsScrollRef}>
                                {activeDistricts.map((dist, idx) => (
                                    <button
                                        key={idx}
                                        className={`dist-tab-pill cursor-pointer ${selectedDistrict === dist ? 'active' : ''}`}
                                        onClick={() => setSelectedDistrict(dist)}
                                    >
                                        {dist}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Table Grouped by District */}
                    <div className="table-panel">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>DISTRICT / CONSTITUENCY</th>
                                    {sortedParties?.map((party, pIdx) => (
                                        <th key={pIdx}>{party.short_name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {selectedDistrict ? (
                                    <>

                                        {filteredConsts.length > 0 ? (
                                            filteredConsts.map((cons, cIdx) => (
                                                <tr key={cIdx}>
                                                    <td>
                                                        <div className="jilla-container">
                                                            <h4 className="j-guj">{cons.name}</h4>

                                                        </div>
                                                    </td>
                                                    {sortedParties?.map((party, pIdx) => {
                                                        const partyColor = party.short_name === 'OTH' ? '#0ea5e9' : party.color_code;
                                                        if (party.short_name === 'OTH') {
                                                            const mainPartyNames = sortedParties.filter(p => p.short_name !== 'OTH').map(p => p.short_name);
                                                            const others = cons.candidates?.filter(can => !mainPartyNames.includes(can.party_short_name));
                                                            return (
                                                                <td key={pIdx}>
                                                                    <div
                                                                        className={`party-badge oth ${others?.length === 0 ? 'empty' : ''}`}
                                                                        style={others?.length > 0 ? {
                                                                            color: partyColor,
                                                                            backgroundColor: hexToRGBA(partyColor, 0.08),
                                                                            borderLeftColor: partyColor
                                                                        } : {}}
                                                                    >
                                                                        {others?.length > 0 ? (
                                                                            <div className="oth-stack">
                                                                                {others.map((oc, oidx) => (
                                                                                    <div key={oidx} className="oc-item">
                                                                                        {oc.name} <span className="oc-party">({oc.party_short_name})</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        ) : '-'}
                                                                    </div>
                                                                </td>
                                                            );
                                                        }

                                                        const candidate = cons.candidates?.find(can => can.party_short_name === party.short_name);
                                                        return (
                                                            <td key={pIdx}>
                                                                <div
                                                                    className={`party-badge ${party.short_name.toLowerCase()} ${!candidate ? 'empty' : ''}`}
                                                                    style={candidate ? {
                                                                        color: partyColor,
                                                                        backgroundColor: hexToRGBA(partyColor, 0.08),
                                                                        borderLeftColor: partyColor
                                                                    } : {}}
                                                                >
                                                                    {candidate ? candidate.name : '-'}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={1 + (sortedParties?.length || 0)} style={{ textAlign: 'center', padding: '20px' }}>No matches found for "{searchTerm}" in this district.</td>
                                            </tr>
                                        )}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan={1 + (sortedParties?.length || 0)} style={{ textAlign: 'center', padding: '40px' }}>Loading Data...</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <HomeTaboola />
        </>
    );
};

export default AssemblyEle;
